import React, { useState, useRef, useEffect } from "react";
import "./TextChat.css";
import { apiPost, apiPut } from "../../services/api";
import { TranscriptEntry } from "../../types";

interface TextChatProps {
  sessionId: string;
  onSessionEnd: () => void;
}

export function TextChat({ sessionId, onSessionEnd }: TextChatProps) {
  const [messages, setMessages] = useState<TranscriptEntry[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [paused, setPaused] = useState(false);
  const [ending, setEnding] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending || paused) return;

    const userMessage = input.trim();
    setInput("");
    setSending(true);

    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "rep", message: userMessage, timestamp: now }]);

    try {
      const result = await apiPost<{ aiResponse: string; timestamp: string }>(
        `/api/sessions/${sessionId}/message`,
        { message: userMessage },
      );
      setMessages((prev) => [...prev, { role: "prospect", message: result.aiResponse, timestamp: result.timestamp }]);
    } catch (err) {
      console.error("Failed to send message:", err);
      setMessages((prev) => [...prev, { role: "prospect", message: "⚠️ Failed to get response. Try again.", timestamp: now }]);
    } finally {
      setSending(false);
    }
  };

  const handlePause = async () => {
    try {
      await apiPut(`/api/sessions/${sessionId}/${paused ? "resume" : "pause"}`);
      setPaused(!paused);
    } catch (err) {
      console.error("Failed to toggle pause:", err);
    }
  };

  const handleEnd = async () => {
    setEnding(true);
    try {
      await apiPut(`/api/sessions/${sessionId}/end`);
      onSessionEnd();
    } catch (err) {
      console.error("Failed to end session:", err);
      setEnding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="protrain-chat">
      <div className="protrain-chat-controls">
        <button className="protrain-chat-control-btn" onClick={handlePause}>
          {paused ? "▶️ Resume" : "⏸️ Pause"}
        </button>
        <button className="protrain-chat-control-btn protrain-chat-control-btn--end" onClick={handleEnd} disabled={ending}>
          {ending ? "⏳ Ending & Scoring..." : "⏹️ End Session"}
        </button>
      </div>

      <div className="protrain-chat-messages">
        {messages.length === 0 && (
          <div className="protrain-chat-empty">Start the conversation — introduce yourself to the prospect.</div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`protrain-chat-message protrain-chat-message--${msg.role}`}>
            <div className="protrain-chat-message-label">{msg.role === "rep" ? "You" : "Prospect"}</div>
            <div className="protrain-chat-message-text">{msg.message}</div>
          </div>
        ))}
        {sending && (
          <div className="protrain-chat-message protrain-chat-message--prospect">
            <div className="protrain-chat-message-label">Prospect</div>
            <div className="protrain-chat-message-text protrain-chat-typing">Typing...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="protrain-chat-input-area">
        <textarea
          className="protrain-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={paused ? "Session paused..." : "Type your message..."}
          disabled={paused || sending}
          rows={2}
        />
        <button
          className="protrain-chat-send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || sending || paused}
        >
          Send
        </button>
      </div>
    </div>
  );
}
